import React, { useState, useEffect, useRef } from "react";
import { NavLink, useLocation } from "react-router-dom";
import "./Header.css";

const NAV_LINKS = [
  { href: "/#about",        label: "About"       },
  { href: "/#technology",   label: "Technology"  },
  { href: "/#products",     label: "Products"    },
  { href: "/#testimonials", label: "Testimonials"},
  { href: "/#contact",      label: "Contact"     },
];

// Smooth-scroll to a hash, offsetting for the fixed header
function scrollToHash(hash) {
  const el = document.querySelector(hash);
  if (!el) return;
  const top = el.getBoundingClientRect().top + window.scrollY - 72;
  window.scrollTo({ top, behavior: "smooth" });
}

function Header() {
  const [isOpen, setIsOpen]       = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [active, setActive]       = useState("");
  const navRef = useRef(null);
  const location = useLocation();

  // Scroll detection → frosted-glass effect
  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    const onOutside = (e) => {
      if (navRef.current && !navRef.current.contains(e.target))
        setIsOpen(false);
    };
    document.addEventListener("mousedown", onOutside);
    return () => document.removeEventListener("mousedown", onOutside);
  }, [isOpen]);

  // Lock body scroll when drawer is open
  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [isOpen]);

  // Highlight active section via IntersectionObserver
  useEffect(() => {
    if (location.pathname !== "/") { setActive(""); return; }
    const ids = NAV_LINKS.map((l) => l.href.replace("/#", ""));
    const observers = ids.map((id) => {
      const el = document.getElementById(id);
      if (!el) return null;
      const obs = new IntersectionObserver(
        ([entry]) => { if (entry.isIntersecting) setActive(`/#${id}`); },
        { threshold: 0.3 }
      );
      obs.observe(el);
      return obs;
    });
    return () => observers.forEach((o) => o && o.disconnect());
  }, [location.pathname]);

  const close = () => setIsOpen(false);

  const handleNavClick = (e, href) => {
    e.preventDefault();
    close();
    const hash = href.replace("/", "");   // "/#about" → "#about"
    if (location.pathname === "/") {
      scrollToHash(hash);
    } else {
      // Navigate home first, then scroll after the page loads
      window.location.href = href;
    }
  };

  return (
    <header className={`header${isScrolled ? " scrolled" : ""}`} ref={navRef}>
      <div className="navbar">

        {/* ── Logo ── */}
        <NavLink to="/" className="brand-logo" onClick={close}>
          <img src="/logo-navbar.png" alt="Oster Battery" className="brand-logo-img" />
        </NavLink>

        {/* ── Desktop nav ── */}
        <nav className="nav-links" aria-label="Main navigation">
          {NAV_LINKS.map(({ href, label }) => (
            <a
              key={href}
              href={href}
              className={`nav-item${active === href ? " active" : ""}`}
              onClick={(e) => handleNavClick(e, href)}
            >
              {label}
            </a>
          ))}
        </nav>

        {/* ── Right: CTA + hamburger ── */}
        <div className="nav-right">
          <a
            href="/#contact"
            className="nav-cta"
            onClick={(e) => handleNavClick(e, "/#contact")}
          >
            Enquire Now
          </a>
          <button
            className={`hamburger${isOpen ? " open" : ""}`}
            onClick={() => setIsOpen((p) => !p)}
            aria-label={isOpen ? "Close menu" : "Open menu"}
            aria-expanded={isOpen}
          >
            <span className="bar" />
            <span className="bar" />
            <span className="bar bar--short" />
          </button>
        </div>

      </div>

      {/* ── Mobile drawer ── */}
      <div className={`mobile-drawer${isOpen ? " open" : ""}`} aria-hidden={!isOpen}>
        <nav className="mobile-nav">
          {NAV_LINKS.map(({ href, label }) => (
            <a
              key={href}
              href={href}
              className={`nav-item${active === href ? " active" : ""}`}
              onClick={(e) => handleNavClick(e, href)}
            >
              {label}
            </a>
          ))}
        </nav>
        <a
          href="/#contact"
          className="mobile-cta"
          onClick={(e) => handleNavClick(e, "/#contact")}
        >
          Enquire Now
        </a>
      </div>
    </header>
  );
}

export default Header;
